import { Avatar, Button, CircularProgress, Dialog, DialogActions, DialogContent, Grid, TextField, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { createTheme } from '@mui/material/styles';
import { memo, useEffect, useState } from "react";
import IPaciente from "../../../types/Paciente";
import PacienteService from "../../../services/PacienteService";
import ConfigUtils from "../../../utils/Config";
import PacientesUtils from "../../../utils/Pacientes";
import IAlert from "../../../types/Alert";
import IMinPaciente from "../../../types/MinPaciente";
import { useNavigate } from "react-router-dom";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import * as constantes from "../../../common/Constantes";
import { usePhoneInput } from "../../../hooks/usePhoneInput";


interface PacienteDialogProps {
    open: boolean;
    onCancel: () => void;
    pacientes: IPaciente[];
    setPacientes: (pacientes: IPaciente[]) => void;
    setPacienteId: (id: string) => void;
    setAlert: (alert: IAlert) => void;
}

const PacienteDialogComponent = ({ open, onCancel, pacientes, setPacientes, setPacienteId, setAlert }: PacienteDialogProps) => {
    
    const theme = createTheme();
    const history = useNavigate();

    const [nombre, setNombre] = useState('');
    const [apellidos, setApellidos] = useState('');
    const [errorNombre, setErrorNombre] = useState(false);
    const [errorApellidos, setErrorApellidos] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
      value: telefono,
      setValue: setTelefono,
      error: errorTelefono,
      validate: validateTelefono,
      reset: resetTelefono,
      normalized: telefonoNormalizado
    } = usePhoneInput();

    useEffect(() => {
      if (!open) {
        setNombre('');
        setApellidos('');
        resetTelefono();
        setErrorNombre(false);
        setErrorApellidos(false);
      }
    }, [open]);
  
    const handleSave = async () => {
        const nombreOk = !!nombre.trim();
        const apellidosOk = !!apellidos.trim();
        const telefonoOk = validateTelefono();

        setErrorNombre(!nombreOk);
        setErrorApellidos(!apellidosOk);

        if (!nombreOk || !apellidosOk || !telefonoOk) return;

        setLoading(true);

        const newPaciente: IMinPaciente = {
            nombre,
            apellidos,
            telefono: telefonoNormalizado,
            userId: ConfigUtils.getUserId()
        };

        try {
            const response = await PacienteService.create(newPaciente);
            const res = PacientesUtils.sortPacientesAlphabetically([...pacientes, response.data]);
            setPacientes(res);
            setPacienteId(response.data._id.toString());
            setAlert({ open: true, severity: 'success', msg: 'Paciente guardado correctamente' });
            onCancel();
        } catch (e: any) {
            if (e.response?.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = {
                    method: 'PacienteDialog.Component.savePaciente',
                    level: 'error',
                    message: e.message,
                    meta: e.response
                };
                LogService.logError(error);
                setAlert({
                    open: true,
                    severity: 'error',
                    msg: e.response?.data?.message
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} maxWidth="lg" fullWidth onClose={onCancel}>
            <DialogContent>
                <Typography variant="h4" sx={{ display: 'inline-flex' }}>
                    <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><AddIcon /></Avatar><div style={{ margin: '10px auto' }}>Nuevo paciente</div>
                </Typography>
                <Grid container spacing={1}>
                    <Grid item md={4} xs={12} style={{ margin: 'auto' }}>
                        <TextField value={nombre} onChange={(e) => setNombre(e.target.value)} variant="outlined" label="Nombre" margin="dense" fullWidth required autoFocus error={errorNombre} helperText={errorNombre? "El nombre es obligatorio" : ""} />
                    </Grid>
                    <Grid item md={4} xs={12} style={{ margin: 'auto' }}>
                        <TextField value={apellidos} onChange={(e) => setApellidos(e.target.value)} variant="outlined" label="Apellidos" margin="dense" fullWidth required error={errorApellidos} helperText={errorApellidos ? "Los apellidos son obligatorios" : ""} />
                    </Grid>
                    <Grid item md={4} xs={12} style={{ margin: 'auto' }}>
                        <TextField value={telefono} onChange={(e) => setTelefono(e.target.value)} variant="outlined" label="Teléfono" margin="dense" fullWidth required error={errorTelefono} helperText={errorTelefono ? "El teléfono no es válido" : ""} />
                    </Grid>
                </Grid>
            </DialogContent>
            {loading ?
                <Grid container>
                    <CircularProgress style={{ margin: '20px auto' }} />
                </Grid> :
                <DialogActions>
                    <Button onClick={handleSave}>Guardar</Button>
                    <Button onClick={onCancel}>Cancelar</Button>
                </DialogActions>
            }
        </Dialog>
    );
};

export const PacienteDialog = memo(PacienteDialogComponent);
export default PacienteDialog;